<template>
  <div class="page-container">
    <PageHeader title="答题结果" show-breadcrumb />

    <SearchPanel>
      <el-input v-model.trim="query.qpName" placeholder="试卷名称" clearable size="small" @keyup.enter.native="handleSearch" />
      <el-select v-model="query.qprStatus" placeholder="答题状态" clearable size="small">
        <el-option label="未提交" :value="1" />
        <el-option label="已提交" :value="2" />
        <el-option label="已批阅" :value="3" />
      </el-select>
      <template #actions>
        <el-button type="primary" icon="el-icon-search" size="small" @click="handleSearch">查询</el-button>
        <el-button icon="el-icon-refresh" size="small" @click="handleReset">重置</el-button>
      </template>
    </SearchPanel>

    <TablePanel title="答题结果列表">
      <el-table :data="list" v-loading="loading" size="small" stripe :height="tableHeight">
        <el-table-column type="index" label="序号" width="80" align="center" />
        <el-table-column prop="qpName" label="试卷名称" min-width="220" align="center" show-overflow-tooltip />
        <el-table-column label="答题状态" min-width="110" align="center">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="statusTagType(row.qprStatus)">{{ statusText(row.qprStatus) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="qprShowScore" label="得分" min-width="100" align="center" />
        <el-table-column label="操作" width="140" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" style="color:#409eff" @click="handleDetail(row)">查看详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        :current-page="page.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.pageSize"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </TablePanel>

    <!-- 答题详情：展示每题题干、选项、用户作答、解析 -->
    <el-dialog :title="detailTitle" :visible.sync="detailVisible" width="860px" v-dialog-drag-resize>
      <div v-loading="detailLoading" class="record-detail">
        <div v-if="!detailList.length && !detailLoading" style="text-align:center; color:#909399; padding:40px 0;">暂无答题明细</div>
        <div v-for="(t, idx) in detailList" :key="idx" class="record-topic">
          <div class="topic-head">
            <span class="topic-no">第 {{ t.qptOrder || (idx + 1) }} 题</span>
            <el-tag size="mini">{{ t.qttName || '-' }}</el-tag>
            <span class="topic-score">{{ t.qtScoreUser != null ? t.qtScoreUser : 0 }} / {{ t.qtScore != null ? t.qtScore : 0 }} 分</span>
            <el-tag size="mini" :type="t.qtScoreRight === 2 ? 'success' : 'danger'">{{ t.qtScoreRight === 2 ? '回答正确' : '回答错误' }}</el-tag>
          </div>
          <div class="topic-name">{{ t.qtName }}</div>
          <img v-if="t.qtNameImage" :src="t.qtNameImage" class="topic-img" />
          <!-- 选项列表：标记正确答案与用户作答 -->
          <div class="topic-options">
            <div
              v-for="opt in (t.questionTopicItemList || [])"
              :key="opt.qtiId"
              class="option"
              :class="{ 'is-right': opt.qtiIsAnswer === 2, 'is-wrong': isUserAnswer(opt.qtiId, t.qptAnswerIds) && opt.qtiIsAnswer !== 2 }"
            >
              <span class="option-value">{{ opt.qtiValue }}</span>
              <el-tag v-if="opt.qtiIsAnswer === 2" size="mini" type="success">正确答案</el-tag>
              <el-tag v-if="isUserAnswer(opt.qtiId, t.qptAnswerIds)" size="mini" type="warning">你的选择</el-tag>
            </div>
          </div>
          <div v-if="t.qtAnalysis" class="topic-analysis"><span class="analysis-label">解析：</span>{{ t.qtAnalysis }}</div>
        </div>
      </div>
      <div slot="footer">
        <el-button size="small" @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'
import { getZqpkQuestionPaperRecordList, getZqpkQuestionPaperRecordDetails } from '@/api/api'

const STATUS_MAP = { 1: { label: '未提交', type: 'info' }, 2: { label: '已提交', type: 'success' }, 3: { label: '已批阅', type: 'warning' } }

export default {
  name: 'QuestionPaperRecord',
  components: { PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      tableHeight: 400,
      list: [],
      query: { qpName: '', qprStatus: '' },
      page: { currentPage: 1, pageSize: 10, total: 0 },
      detailVisible: false,
      detailLoading: false,
      detailTitle: '答题详情',
      detailList: []
    }
  },
  created () { this.fetchList() },
  mounted () {
    this.$nextTick(this.calcTableHeight)
    window.addEventListener('resize', this.calcTableHeight)
  },
  beforeDestroy () { window.removeEventListener('resize', this.calcTableHeight) },
  methods: {
    statusText (s) { return (STATUS_MAP[s] && STATUS_MAP[s].label) || '-' },
    statusTagType (s) { return (STATUS_MAP[s] && STATUS_MAP[s].type) || 'info' },
    // 判断某选项是否为用户作答（qptAnswerIds 兼容字符串/数组）
    isUserAnswer (optId, answerIds) {
      if (optId == null || answerIds == null || answerIds === '') return false
      const arr = Array.isArray(answerIds) ? answerIds : String(answerIds).split(/[,，]/)
      return arr.map(v => String(v).trim()).includes(String(optId))
    },
    calcTableHeight () {
      const el = this.$el.querySelector('.el-table')
      if (!el) return
      this.tableHeight = window.innerHeight - el.getBoundingClientRect().top - 80
    },
    async fetchList () {
      this.loading = true
      try {
        const params = { ...this.query, currentPage: this.page.currentPage, pageSize: this.page.pageSize }
        const res = await getZqpkQuestionPaperRecordList(params)
        this.list = res.data || []
        this.page.total = res.total || 0
      } catch (e) { this.list = []; this.page.total = 0 } finally { this.loading = false }
    },
    handleSearch () { this.page.currentPage = 1; this.fetchList() },
    handleReset () { this.query = { qpName: '', qprStatus: '' }; this.handleSearch() },
    handleSizeChange (size) { this.page.pageSize = size; this.handleSearch() },
    handlePageChange (page) { this.page.currentPage = page; this.fetchList() },
    async handleDetail (row) {
      this.detailTitle = `答题详情 - ${row.qpName || ''}`
      this.detailVisible = true
      this.detailLoading = true
      this.detailList = []
      try {
        const res = await getZqpkQuestionPaperRecordDetails({ qprId: row.qprId })
        this.detailList = res.data || []
      } catch (e) { this.detailList = [] } finally {
        this.detailLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.record-detail { max-height: 60vh; overflow-y: auto; }
.record-topic {
  padding: 16px;
  margin-bottom: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafbfc;
}
.topic-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #475569;
}
.topic-no { font-weight: 600; color: #1e293b; }
.topic-score { color: #64748b; }
.topic-name { font-size: 14px; color: #1f2937; line-height: 1.7; margin-bottom: 10px; }
.topic-img { max-width: 100%; max-height: 200px; border-radius: 8px; margin-bottom: 10px; }
.topic-options { display: flex; flex-direction: column; gap: 8px; }
.option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e5e7eb;
  font-size: 13px;
  &.is-right { background: #ecfdf5; border-color: #6ee7b7; }
  &.is-wrong { background: #fef2f2; border-color: #fca5a5; }
}
.option-value { flex: 1; }
.topic-analysis {
  margin-top: 12px;
  padding: 10px 12px;
  background: #fffbeb;
  border-radius: 6px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.7;
}
.analysis-label { font-weight: 600; color: #d97706; }
</style>
